// src/services/purchaseInvoiceService.js
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';

// Create a new purchase invoice
export const createPurchaseInvoice = async (invoiceData) => {
  try {
    const docRef = await addDoc(collection(db, 'purchaseInvoices'), {
      ...invoiceData,
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date())
    });
    
    return {
      id: docRef.id,
      ...invoiceData
    };
  } catch (error) {
    console.error('Error creating purchase invoice:', error);
    throw new Error('Failed to create purchase invoice');
  }
};

// Get purchase invoices for a vendor
export const getPurchaseInvoices = async (vendorId, options = {}) => {
  try {
    const {
      status,
      search,
      dateRange,
      limit: limitCount = 50
    } = options;

    // Start with basic query - no orderBy to avoid index requirement
    let q = query(
      collection(db, 'purchaseInvoices'),
      where('vendorId', '==', vendorId)
    );

    // Add status filter only if specified
    if (status && status !== 'all') {
      q = query(q, where('status', '==', status));
    }

    // Execute the query
    const querySnapshot = await getDocs(q);
    let invoices = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Apply date range filter on client side
    if (dateRange && dateRange !== 'all') {
      const now = new Date();
      let startDate;

      switch (dateRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarter':
          const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
          startDate = new Date(now.getFullYear(), quarterStartMonth, 1);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        invoices = invoices.filter(invoice => {
          const invoiceDate = invoice.createdAt?.toDate ? invoice.createdAt.toDate() : new Date(invoice.createdAt);
          return invoiceDate >= startDate;
        });
      }
    }

    // Apply search filter on client side
    if (search && search.trim()) {
      const searchLower = search.toLowerCase().trim();
      invoices = invoices.filter(invoice =>
        invoice.invoiceNumber?.toLowerCase().includes(searchLower) ||
        invoice.supplierName?.toLowerCase().includes(searchLower) ||
        invoice.notes?.toLowerCase().includes(searchLower)
      );
    }

    // Sort on client side (newest first)
    invoices.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    });

    // Apply limit on client side
    if (limitCount && invoices.length > limitCount) {
      invoices = invoices.slice(0, limitCount);
    }

    return invoices;
  } catch (error) {
    console.error('Error fetching purchase invoices:', error);
    throw new Error('Failed to fetch purchase invoices');
  }
};

// Get a single purchase invoice by ID
export const getPurchaseInvoiceById = async (invoiceId) => {
  try {
    const docRef = doc(db, 'purchaseInvoices', invoiceId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Purchase invoice not found');
    }
  } catch (error) {
    console.error('Error fetching purchase invoice:', error);
    throw new Error('Failed to fetch purchase invoice');
  }
};

// Update a purchase invoice
export const updatePurchaseInvoice = async (invoiceId, updateData) => {
  try {
    const docRef = doc(db, 'purchaseInvoices', invoiceId);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: Timestamp.fromDate(new Date())
    });
    
    return await getPurchaseInvoiceById(invoiceId);
  } catch (error) {
    console.error('Error updating purchase invoice:', error);
    throw new Error('Failed to update purchase invoice');
  }
};

// Delete a purchase invoice
export const deletePurchaseInvoice = async (invoiceId) => {
  try {
    const docRef = doc(db, 'purchaseInvoices', invoiceId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting purchase invoice:', error);
    throw new Error('Failed to delete purchase invoice');
  }
};

// Get purchase statistics for a vendor
export const getPurchaseStatistics = async (vendorId, options = {}) => {
  try {
    // Simple query without orderBy
    let q = query(
      collection(db, 'purchaseInvoices'),
      where('vendorId', '==', vendorId)
    );

    const querySnapshot = await getDocs(q);
    let invoices = querySnapshot.docs.map(doc => doc.data());

    // Apply date range filter on client side if specified
    const { dateRange } = options;
    if (dateRange && dateRange !== 'all') {
      const now = new Date();
      let startDate;

      switch (dateRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarter':
          const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
          startDate = new Date(now.getFullYear(), quarterStartMonth, 1);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        invoices = invoices.filter(invoice => {
          const invoiceDate = invoice.createdAt?.toDate ? invoice.createdAt.toDate() : new Date(invoice.createdAt);
          return invoiceDate >= startDate;
        });
      }
    }

    // Calculate statistics
    const totalInvoices = invoices.length;
    const totalAmount = invoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);
    const totalGSTPaid = invoices.reduce((sum, invoice) => sum + (invoice.gstAmount || 0), 0);
    const pendingInvoices = invoices.filter(invoice => invoice.status === 'pending').length;
    const receivedInvoices = invoices.filter(invoice => invoice.status === 'received').length;
    const cancelledInvoices = invoices.filter(invoice => invoice.status === 'cancelled').length;

    // Calculate supplier statistics
    const supplierStats = {};
    invoices.forEach(invoice => {
      const supplier = invoice.supplierName;
      if (!supplierStats[supplier]) {
        supplierStats[supplier] = {
          invoiceCount: 0,
          totalAmount: 0,
          totalGST: 0
        };
      }
      supplierStats[supplier].invoiceCount++;
      supplierStats[supplier].totalAmount += invoice.totalAmount || 0;
      supplierStats[supplier].totalGST += invoice.gstAmount || 0;
    });

    // Get top suppliers
    const topSuppliers = Object.entries(supplierStats)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 5);

    // Calculate monthly trends (last 6 months)
    const monthlyTrends = {};
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    invoices.forEach(invoice => {
      const date = invoice.createdAt?.toDate ? invoice.createdAt.toDate() : new Date(invoice.createdAt);
      if (date >= sixMonthsAgo) {
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyTrends[monthKey]) {
          monthlyTrends[monthKey] = {
            month: monthKey,
            invoiceCount: 0,
            totalAmount: 0,
            totalGST: 0
          };
        }
        monthlyTrends[monthKey].invoiceCount++;
        monthlyTrends[monthKey].totalAmount += invoice.totalAmount || 0;
        monthlyTrends[monthKey].totalGST += invoice.gstAmount || 0;
      }
    });

    return {
      totalInvoices,
      totalAmount,
      totalGSTPaid,
      pendingInvoices,
      receivedInvoices,
      cancelledInvoices,
      topSuppliers,
      monthlyTrends: Object.values(monthlyTrends).sort((a, b) => a.month.localeCompare(b.month))
    };
  } catch (error) {
    console.error('Error fetching purchase statistics:', error);
    throw new Error('Failed to fetch purchase statistics');
  }
};

// Get purchase history for a specific product
export const getProductPurchaseHistory = async (vendorId, productId, productName) => {
  try {
    const q = query(
      collection(db, 'purchaseInvoices'),
      where('vendorId', '==', vendorId)
    );

    const querySnapshot = await getDocs(q);
    const purchases = [];

    querySnapshot.docs.forEach(doc => {
      const invoice = { id: doc.id, ...doc.data() };
      
      // Find items in this invoice that match the product
      const matchingItems = invoice.items?.filter(item => 
        item.productId === productId || 
        (productName && item.productName?.toLowerCase() === productName.toLowerCase())
      ) || [];

      matchingItems.forEach(item => {
        purchases.push({
          invoiceId: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          supplierName: invoice.supplierName,
          purchaseDate: invoice.purchaseDate,
          quantity: item.quantity,
          rate: item.rate,
          amount: item.amount,
          gstRate: item.gstRate,
          createdAt: invoice.createdAt,
          status: invoice.status
        });
      });
    });

    // Sort by purchase date (newest first)
    purchases.sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));

    return purchases;
  } catch (error) {
    console.error('Error fetching product purchase history:', error);
    throw new Error('Failed to fetch product purchase history');
  }
};

// Generate purchase invoice number
export const generatePurchaseInvoiceNumber = async (vendorId) => {
  try {
    // Simple query without orderBy
    const q = query(
      collection(db, 'purchaseInvoices'),
      where('vendorId', '==', vendorId)
    );

    const querySnapshot = await getDocs(q);
    
    const prefix = 'PUR';
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    
    let sequence = 1;
    
    if (!querySnapshot.empty) {
      // Get all invoices and find the latest one on client side
      const invoices = querySnapshot.docs.map(doc => doc.data());
      const currentMonthInvoices = invoices.filter(invoice => {
        const match = invoice.invoiceNumber?.match(/PUR-(\d{4})-(\d{2})-(\d+)/);
        return match && match[1] === year.toString() && match[2] === month;
      });
      
      if (currentMonthInvoices.length > 0) {
        const sequences = currentMonthInvoices.map(invoice => {
          const match = invoice.invoiceNumber?.match(/PUR-(\d{4})-(\d{2})-(\d+)/);
          return match ? parseInt(match[3]) : 0;
        });
        sequence = Math.max(...sequences) + 1;
      }
    }
    
    return `${prefix}-${year}-${month}-${String(sequence).padStart(3, '0')}`;
  } catch (error) {
    console.error('Error generating purchase invoice number:', error);
    // Fallback to timestamp-based number
    const timestamp = Date.now().toString().slice(-6);
    return `PUR-${new Date().getFullYear()}-${timestamp}`;
  }
};

// Update product stock based on purchase
export const updateProductStockFromPurchase = async (productId, quantity, operation = 'add') => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);
    
    if (productSnap.exists()) {
      const currentStock = productSnap.data().stock || 0;
      const newStock = operation === 'add' 
        ? currentStock + quantity 
        : Math.max(0, currentStock - quantity);
      
      await updateDoc(productRef, {
        stock: newStock,
        updatedAt: Timestamp.fromDate(new Date())
      });
      
      return newStock;
    } else {
      throw new Error('Product not found');
    }
  } catch (error) {
    console.error('Error updating product stock:', error);
    throw new Error('Failed to update product stock');
  }
};

export default {
  createPurchaseInvoice,
  getPurchaseInvoices,
  getPurchaseInvoiceById,
  updatePurchaseInvoice,
  deletePurchaseInvoice,
  getPurchaseStatistics,
  getProductPurchaseHistory,
  generatePurchaseInvoiceNumber,
  updateProductStockFromPurchase
};